import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { QuizzesDataService, QuizzesColumnsService } from '../../services';
import { DialogQuizFormComponent } from './quiz-form/quiz-form.component';
import { DialogQuizDetailsComponent } from './quiz-details/quiz-details.component';
import { Quiz, QuizzesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-quizzes',
  templateUrl: './quizzes.component.html',
  styleUrls: ['./quizzes.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class QuizzesComponent implements OnInit, OnDestroy {

  private quizAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private quizEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private quizDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public quizToDelete: Quiz = {};
  public quizzes: Quiz[] = [];
  public quiz: Quiz = {};
  public selectedQuizzes: Quiz[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private quizzesDataService: QuizzesDataService,
    private quizzesColumnsService: QuizzesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('QuizzesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Quizzes - Management');
    this.primengConfig.ripple = true;
    this.getQuizzes();
  }

  ngOnDestroy(): void {
    console.log('QuizzesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getQuizzes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.quizzesDataService.getQuizzes(this.parameters).subscribe((response: QuizzesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.quizzes = response.results;
        } else {
          this.quizzes = this.quizzes.concat(response.results);
        }
        if (response.inlineCount > this.quizzes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getQuizzes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZZESNOTLOADED') });
    });
  }

  importQuizzes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.quizzesDataService.addQuizzes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('QUIZZESCREATED') });
                if (proceed === j) {
                  this.getQuizzes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZZESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZZESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.quizzesDataService.updateQuizzes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('QUIZZESUPDATED') });
                if (proceed === j) {
                  this.getQuizzes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZZESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZZESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.quizzesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('quizzes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedQuizzes() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Quizzes?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.quizzesDataService.deleteQuizzes(this.selectedQuizzes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Quizzes Deleted', life: 3000 });
            this.getQuizzes();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Quizzes not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Quizzes not deleted' });
        });
        this.selectedQuizzes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.quizzes, this.columns, 'Quizzes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.quizzes, this.columns, 'Quizzes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.quizzes, this.columns, 'Quizzes');
  }

  refresh() {
    this.quizzesDataService.clearCache();
    this.getQuizzes();
  }

  quizAdd(): void {
    if (!this.navigation.getParamValue('quizzes.addInDialog', true)) {
      this.router.navigate(['/quizzes/add']);
      return;
    }
    this.quizAddDialog = this.dialogService.open(DialogQuizFormComponent, {
      header: '', // this.translate.instant('NEWQUIZ'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        quiz: { id: null },
        quizTypes: this.quizzesColumnsService.quizTypes,
        users: this.quizzesColumnsService.users,
      },
    });

    this.quizAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Quiz added' });
        this.getQuizzes();
      }
    });
  }

  quizEdit(quiz: Quiz): void {
    if (!this.navigation.getParamValue('quizzes.editInDialog', true)) {
      this.router.navigate(['/quizzes/edit/' + quiz.id]);
      return;
    }
    this.quizEditDialog = this.dialogService.open(DialogQuizFormComponent, {
      header: '', // this.translate.instant('EDITQUIZ') + ' ' + quiz.title,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        quiz: quiz,
        quizTypes: this.quizzesColumnsService.quizTypes,
        users: this.quizzesColumnsService.users,
      },
    });

    this.quizEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Quiz updated' });
        this.getQuizzes();
      }
    });
  }

  quizDetails(quiz: Quiz): void {
    if (!this.navigation.getParamValue('quizzes.detailsInDialog', true)) {
      this.router.navigate(['/quizzes/details/' + quiz.id]);
      return;
    }
    this.quizDetailsDialog = this.dialogService.open(DialogQuizDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        quiz: quiz,
      }
    });
  }

  quizDelete(quiz: Quiz): void {
    this.quizToDelete = quiz;
    this.messageService.clear();
    this.messageService.add({
      key: 'quizDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + quiz.title + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.quizzesDataService.deleteQuiz(this.quizToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Quiz deleted' });
      this.getQuizzes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Quiz not deleted' });
    });
  }

  onDeleteReject(): void {
    this.quizToDelete = {};
    this.messageService.clear('quizDelete');
  }

}
